import { startCase } from "lodash";
import { t } from "@/i18n-js/instance";
import { MEMBER_FIELDS } from "../components/common/copilot/PreviewCards/MemberCard";
import { MEMBER_PROFILE_FIELDS } from "../components/common/copilot/PreviewCards/MemberProfileCard";
import { PROFILE_FIELDS } from "../components/common/copilot/PreviewCards/ProfileFieldCard";
import { SPACE_FIELDS } from "../components/common/copilot/PreviewCards/SpaceCard";
import { getFormattedValue } from "./previewValueFormatterUtils";

type FieldValue = string | number | boolean | string[];

interface Field {
  label: string;
  value: FieldValue;
}

interface FieldConfig {
  defaultValue?: unknown;
  label: string;
  type: string;
}

const FIELD_CONFIGS: Record<string, Record<string, FieldConfig>> = {
  space: SPACE_FIELDS,
  profile_field: PROFILE_FIELDS,
  member_profile: MEMBER_PROFILE_FIELDS,
};

const LISTING_CARD_TITLES: Record<string, string> = {
  member_listing_card: t("help_widget.copilot.chat_card.members_matching"),
  invitation_link_listing_card: t(
    "help_widget.copilot.chat_card.invitation_links",
  ),
  member_tag_listing_card: t("help_widget.copilot.chat_card.member_tags"),
  profile_field_listing_card: t("help_widget.copilot.chat_card.profile_fields"),
  space_group_listing_card: t("help_widget.copilot.chat_card.space_groups"),
  space_listing_card: t("help_widget.copilot.chat_card.spaces"),
  access_group_listing_card: t("help_widget.copilot.chat_card.access_groups"),
  community_segment_listing_card: t(
    "help_widget.copilot.chat_card.community_segments",
  ),
};

const isFieldValue = (value: unknown): value is FieldValue => {
  if (typeof value === "string") return true;
  if (typeof value === "number") return true;
  if (typeof value === "boolean") return true;
  if (Array.isArray(value)) {
    return value.every(item => typeof item === "string");
  }
  return false;
};

const asString = (value: unknown): string =>
  typeof value === "string" ? value : "";

const toParamValue = (value: FieldValue): string | string[] | boolean => {
  if (typeof value === "number") return String(value);
  return value;
};

const formatField = (field: Field, type?: string): string =>
  `• ${field.label}: ${getFormattedValue(toParamValue(field.value), type || "string")}`;

const formatLink = (url: string, title?: string): string =>
  title ? `Link: [${title}](${url})` : `Link: ${url}`;

const joinSections = (...sections: Array<string | undefined>): string =>
  sections.filter(Boolean).join("\n\n");

const extractConfiguredFields = (
  params: Record<string, unknown>,
  fieldConfig: Record<string, FieldConfig>,
): Field[] => {
  const fields: Field[] = [];

  Object.entries(fieldConfig).forEach(([key, config]) => {
    const value = params[key] ?? config.defaultValue;
    if (value != null && isFieldValue(value)) {
      fields.push({ label: config.label, value });
    }
  });

  return fields;
};

const extractAdditionalFields = (
  params: Record<string, unknown>,
  fieldConfig: Record<string, FieldConfig>,
): Field[] => {
  const fields: Field[] = [];

  Object.entries(params).forEach(([key, value]) => {
    if (!(key in fieldConfig) && value != null && isFieldValue(value)) {
      fields.push({ label: startCase(key), value });
    }
  });

  return fields;
};

const extractAllFields = (
  params: Record<string, unknown>,
  fieldConfig: Record<string, FieldConfig>,
): Field[] => [
  ...extractConfiguredFields(params, fieldConfig),
  ...extractAdditionalFields(params, fieldConfig),
];

const formatFieldsSection = (
  fields: Field[],
  fieldConfig: Record<string, FieldConfig>,
): string =>
  fields
    .map(field => {
      const config = Object.values(fieldConfig).find(
        c => c.label === field.label,
      );
      return formatField(field, config?.type);
    })
    .join("\n");

const formatMemberFields = (members: unknown[]): string =>
  members
    .map(member => {
      if (typeof member !== "object" || member === null) return "";
      const fields = extractConfiguredFields(
        member as Record<string, unknown>,
        MEMBER_FIELDS,
      );
      return formatFieldsSection(fields, MEMBER_FIELDS);
    })
    .filter(Boolean)
    .join("\n\n");

export const formatPreviewCard = (content: {
  title?: string;
  url?: string;
  resource: string;
  params: Record<string, unknown>;
}): string => {
  const { resource, params, url, title } = content;

  if (resource === "member") {
    const members = Array.isArray(params.members) ? params.members : [];
    const fieldsText = formatMemberFields(members);
    const linkText = url ? formatLink(url, title) : "";
    return joinSections(title, fieldsText, linkText);
  }

  const fieldConfig = FIELD_CONFIGS[resource] || {};
  const fields = extractAllFields(params, fieldConfig);
  const fieldsText = formatFieldsSection(fields, fieldConfig);
  const linkText = url ? formatLink(url, title) : "";

  return joinSections(title, fieldsText, linkText);
};

export const formatConfirmationCard = (content: {
  title?: string;
  params: Record<string, unknown>;
}): string => {
  const { title, params } = content;

  const fields: Field[] = [];
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== "" && isFieldValue(value)) {
      fields.push({ label: startCase(key), value });
    }
  });

  const fieldsText = fields.map(field => formatField(field)).join("\n");
  return joinSections(title, fieldsText);
};

interface ListingItem {
  details: string[];
  label: string;
}

const getSpaceTypeLabel = (spaceType: string): string => {
  const spaceNameLabel = t(
    `settings.spaces.space_types.${spaceType}`,
  ).toLowerCase();
  return t("help_widget.copilot.chat_card.space_types", {
    space_type: spaceNameLabel,
  });
};

const getVisibilityLabel = (isPublic: boolean): string => {
  const statusKey = isPublic ? "public" : "private";
  const translationKey = `help_widget.copilot.chat_card.tag_visibility.${statusKey}`;
  const translated = t(translationKey);
  return translated === translationKey ? statusKey : translated;
};

const extractListingItem = (resource: Record<string, unknown>): ListingItem => {
  const label =
    asString(resource.name) ||
    asString(resource.label) ||
    asString(resource.title) ||
    "";

  const details: string[] = [];

  const email = asString(resource.email);
  if (email) details.push(email);

  const status = asString(resource.status);
  if (status) details.push(status);

  const spaceType = asString(resource.space_type);
  if (spaceType) details.push(getSpaceTypeLabel(spaceType));

  const fieldType = asString(resource.field_type);
  if (fieldType) details.push(fieldType);

  if (resource.is_public !== undefined) {
    details.push(getVisibilityLabel(Boolean(resource.is_public)));
  }

  return { label, details };
};

const formatListingItem = (item: ListingItem, index: number): string => {
  const detailsText = item.details.length
    ? ` (${item.details.join(", ")})`
    : "";
  return `${index + 1}. ${item.label}${detailsText}`;
};

export const formatListingCard = (
  content: { resources: Array<Record<string, unknown>> },
  cardType: string,
): string => {
  const { resources } = content;

  if (!resources?.length) return "";

  const title = LISTING_CARD_TITLES[cardType] || "";
  const items = resources.map(extractListingItem);
  const itemsText = items.map(formatListingItem).join("\n");

  return joinSections(title, itemsText);
};

export const formatLinkCard = (content: {
  title?: string;
  url?: string;
}): string => {
  const { title, url } = content;
  return url ? formatLink(url, title) : title || "";
};
